import {
  setIsCertificateTypesRequested,
  type CertificateType,
} from '../../../Store/DataSlice'
import './CertificateTypeEdit.scss'
import { useEffect, useMemo, useState } from 'react'
import { useLocalization } from '../../../Hooks/useLocalization'
import { Button } from '../../../Components/Button/Button'
import { useDispatch, useSelector } from 'react-redux'
import { setDestinationView } from '../../../Store/InterfaceSlice'
import { type RootState } from '../../../Store/Store'
import { getAuthorizationHeader } from '../../../Orchestration/Authentication'
import { CheckboxOption } from '../../../Components/CheckboxOption/CheckboxOption'
import { getNaceSelectOptions } from '../../../Utils/NaceCodes'
import { MultiSelect } from '../../../Components/MultiSelect/MultiSelect'
import { LoadingSpinner } from '../../../Components/LoadingSpinner/LoadingSpinner'

interface CertificateTypeEditProps {
  name: string
}
export function CertificateTypeEdit({ name }: CertificateTypeEditProps) {
  const dispatch = useDispatch()
  const localization = useLocalization('certificate-type-add')
  const [allNaceSelectOptions] = useState(getNaceSelectOptions())

  const allCertificateTypes = useSelector(
      (state: RootState) => state.data.certificateTypes
  )

  const isCertificateTypesRequested = useSelector(
    (state: RootState) => state.data.isCertificateTypesRequested
  )
  useEffect(() => {
    if (!isCertificateTypesRequested)
      dispatch(setIsCertificateTypesRequested(true))
  }, [isCertificateTypesRequested, dispatch])

  const baseCertificateType = useSelector((state: RootState) =>
    state.data.certificateTypes.find((c) => c.name === name)
  )
  const [certificateTypeChange, setCertificateTypeChange] = useState<
    Partial<CertificateType>
  >({})
  const certificateType = useMemo(
    () =>
      baseCertificateType !== undefined
        ? { ...baseCertificateType, ...certificateTypeChange }
        : undefined,
    [baseCertificateType, certificateTypeChange]
  )

  const [validationErrors, setValidationErrors] = useState<validationError[]>(
    []
  )
  useEffect(() => {
    const newValidationErrors: validationError[] = []
    if (!certificateType) return

    setValidationErrors(newValidationErrors)
  }, [certificateType])

  const [saveInProgress, setSaveInProgress] = useState(false)
  const saveCertificate = () => {
    setSaveInProgress(true)

    fetch(
      `https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/certificate-type-add`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthorizationHeader(),
        },
        body: JSON.stringify({
          certificateType,
        }),
      }
    )
      .then(() => {
        dispatch(setIsCertificateTypesRequested(false))
        setTimeout(() => {
          dispatch(
            setDestinationView({ destinationView: 'certificate-type-list' })
          )
        }, 1)
      })
      .catch((err) => {
        console.warn(`fetch certificate-type-add failed: `, err)
      })
  }

  if (!certificateType) return null

  return (
    <div className="certificate-type-edit">
      {saveInProgress ? (
        <div className="save-in-progress">
          <LoadingSpinner color="dark" />
          {localization.text(
            'certificate-type-save-in-progress',
            'Het certificaat type word momenteel opgeslagen.'
          )}
        </div>
      ) : !baseCertificateType ? (
        <div className="loading">
          <LoadingSpinner color="dark" />
          {localization.text(
            'certificate-type-is-loading',
            'Het certificaat type word momenteel ingeladen.'
          )}
        </div>
      ) : (
        <>
          <div className="display-field">
            <div className="display-label">
              {localization.text('name', 'Naam')}
            </div>
            <div className="display-value">{certificateType.name}</div>
          </div>

          <CheckboxOption
            label={localization.text('isActive', 'Is actief')}
            isReadOnly={saveInProgress}
            onChange={(newValue) => {
              setCertificateTypeChange((previous) => ({
                ...previous,
                isActive: newValue,
              }))
            }}
            value={certificateType.isActive}
          />

          <MultiSelect
            label={localization.text(
              'naceLimitation',
              'Beperkt tot NACE codes'
            )}
            isReadOnly={saveInProgress}
            items={allNaceSelectOptions}
            onChange={(items) => {
              setCertificateTypeChange((p) => ({
                ...p,
                naceLimitation: items,
              }))
            }}
            values={certificateType.naceLimitation}
          />

          <MultiSelect
              label={localization.text(
                  'requiredCertificateNumbers',
                  'Vereiste certificaat types'
              )}
              isReadOnly={saveInProgress}
              items={allCertificateTypes.map(type => { return { label: type.name, value: type.name} })}
              onChange={(items) => {
                setCertificateTypeChange((p) => ({
                  ...p,
                  requiredCertificateNumbers: items,
                }))
              }}
              values={certificateType.requiredCertificateNumbers}
          />

          <div className="buttons">
            <Button
              importance={'primary'}
              text={localization.text(
                'safe-edit-button',
                'Pas certificaat type aan'
              )}
              onClick={saveCertificate}
              disabled={validationErrors.length > 0 || saveInProgress}
            />
          </div>
        </>
      )}
    </div>
  )
}

interface validationError {
  property: string
  type: 'isRequired' | 'format'
}
